var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import produce from 'immer';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useError } from '../Components/ErrorBoundary';
import { CancelledRequest } from '../Shared/HttpRequestExecutor';
import { useDebounce } from './useDebounce';
export var UseAsyncStatus;
(function (UseAsyncStatus) {
    UseAsyncStatus[UseAsyncStatus["Init"] = 0] = "Init";
    UseAsyncStatus[UseAsyncStatus["Loading"] = 1] = "Loading";
    UseAsyncStatus[UseAsyncStatus["Loaded"] = 2] = "Loaded";
    UseAsyncStatus[UseAsyncStatus["PermissionError"] = 3] = "PermissionError";
    UseAsyncStatus[UseAsyncStatus["Reloading"] = 4] = "Reloading"; // Used for every following load
})(UseAsyncStatus || (UseAsyncStatus = {}));
var asyncReducer = produce(function (state, action) {
    switch (action.type) {
        case 'Reset':
            state.status = UseAsyncStatus.Init;
            state.loadCount = 0;
            state.error = '';
            state.data = action.data;
            break;
        case 'Loading':
            state.status = state.loadCount === 0 ? UseAsyncStatus.Loading : UseAsyncStatus.Reloading;
            state.error = '';
            break;
        case 'Loaded':
            state.status = UseAsyncStatus.Loaded;
            state.loadCount = state.loadCount + 1;
            state.error = '';
            state.data = action.data;
            break;
        case 'SetData':
            state.data = action.data;
            break;
    }
});
function paramsToString() {
    var params = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        params[_i] = arguments[_i];
    }
    return params
        .map(function (it) {
        if (typeof it === 'object')
            return JSON.stringify(it);
        return it.toString();
    })
        .join('-');
}
export function useAsync(config) {
    var throwError = useError();
    var cacheRef = useRef(new Map());
    var cache = cacheRef.current;
    var requestCountRef = useRef(0);
    var initialState = {
        loadCount: 0,
        status: UseAsyncStatus.Init,
        data: config.defaultValue !== undefined ? config.defaultValue : null
    };
    function reset() {
        requestCountRef.current = requestCountRef.current + 1;
        dispatch({ type: 'Reset', data: config.defaultValue !== undefined ? config.defaultValue : null });
    }
    var _a = __read(useReducer(asyncReducer, initialState), 2), asyncState = _a[0], dispatch = _a[1];
    // Used for instant query
    function getApi() {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        return __awaiter(this, void 0, void 0, function () {
            var currentRequestCount, key;
            return __generator(this, function (_a) {
                requestCountRef.current = requestCountRef.current + 1;
                currentRequestCount = requestCountRef.current;
                key = paramsToString.apply(void 0, __spreadArray([], __read(params), false));
                dispatch({ type: 'Loading' });
                if (config.useCache && cache.has(key)) {
                    dispatch({ type: 'Loaded', data: cache.get(key) });
                }
                else {
                    config.getApi.apply(config, __spreadArray([config.httpConfig], __read(params), false)).then(function (newData) {
                        if (newData instanceof CancelledRequest)
                            return;
                        if (requestCountRef.current === currentRequestCount) {
                            // Caching might cause memory leak. Watch this.
                            if (config.useCache) {
                                cache.set(key, newData);
                            }
                            dispatch({ type: 'Loaded', data: newData });
                        }
                    }, throwError);
                }
                return [2 /*return*/];
            });
        });
    }
    var _b = __read(useState(null), 2), searchArguments = _b[0], setSearchArguments = _b[1];
    // Effect for calling load on init
    useEffect(function () {
        if (!config.loadOnInit)
            return;
        getApi.apply(void 0, __spreadArray([], __read(config.loadOnInit), false));
    }, []);
    function debouncedGetApi() {
        var newFetchArguments = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            newFetchArguments[_i] = arguments[_i];
        }
        setSearchArguments(newFetchArguments);
    }
    var debouncedSearchArguments = useDebounce(searchArguments);
    useEffect(function () {
        if (debouncedSearchArguments)
            getApi.apply(void 0, __spreadArray([], __read(debouncedSearchArguments), false));
    }, [debouncedSearchArguments]);
    var dataRef = useRef(asyncState);
    dataRef.current = asyncState.data;
    var updateData = function (action) {
        dispatch({ type: 'SetData', data: action(dataRef.current) });
    };
    function setData(data) {
        dispatch({ type: 'SetData', data: data });
    }
    return __assign(__assign({}, asyncState), { getApi: getApi, debouncedGetApi: debouncedGetApi, setData: setData, updateData: updateData, reset: reset });
}
